import { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";

interface StatsCardProps {
  title: string;
  value: string | number | React.ReactNode;
  icon: LucideIcon;
  trend?: string;
  variant?: "redValueOnBlack" | "blackOnWhite";
  clickable?: boolean;
}

const variantStyles = {
  redValueOnBlack: {
    card: "bg-muted/50 border border-border",
    title: "text-foreground",
    value: "text-primary",
    trend: "text-muted-foreground",
    icon: "text-primary bg-primary/10",
  },
  blackOnWhite: {
    card: "bg-white border border-border",
    title: "text-foreground",
    value: "text-foreground",
    trend: "text-muted-foreground",
    icon: "text-foreground bg-muted",
  },
};

export const StatsCard = ({
  title,
  value,
  icon: Icon,
  trend,
  variant = "blackOnWhite",
  clickable = false,
}: StatsCardProps) => {
  const styles = variantStyles[variant];

  return (
    <Card
      variant="elevated"
      className={`p-5 md:p-6 ${styles.card} ${
        clickable
          ? "cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-100"
          : "transition-all duration-300"
      } animate-fade-in overflow-hidden relative group`}
    >
      {clickable && (
        <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      )}

      <div className="flex items-start justify-between relative z-10">
        <div className="space-y-2 flex-1 min-w-0">
          <p className={`text-xs md:text-sm font-semibold tracking-wide uppercase ${styles.title}`}>
            {title}
          </p>
          <p className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight truncate ${styles.value}`}>
            {value}
          </p>
          {trend && (
            <p className={`text-xs md:text-sm font-medium ${styles.trend}`}>
              {trend}
            </p>
          )}
        </div>
        <div className={`p-2.5 md:p-3 rounded-xl transition-all duration-300 flex-shrink-0 ${styles.icon}`}>
          <Icon className="h-5 w-5 md:h-6 md:w-6" />
        </div>
      </div>
    </Card>
  );
};